import type {
  CustomEmailSender,
  SesDnsRecord,
  SesReadiness,
} from "@redotech/admin-sdk/rpc/schema/support/custom-email-sender";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import ArrowCircleRightIcon from "@redotech/redo-icons/arrow-circle-right_filled.svg";
import CheckCircleIcon from "@redotech/redo-icons/check-circle_filled.svg";
import CircleIcon from "@redotech/redo-icons/circle.svg";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import RefreshIcon from "@redotech/redo-icons/refresh-cw-01.svg";
import { DnsRecordCheckStatus } from "@redotech/redo-marketing-core-common/email/domain-verification";
import { toast } from "@redotech/redo-web/alert";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import {
  RedoBadge,
  type RedoBadgeProps,
} from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import {
  RedoStepIndicator,
  type RedoStep,
  type RedoStepState,
} from "@redotech/redo-web/arbiter-components/step-indicator/redo-step-indicator";
import { copyToClipboard } from "@redotech/redo-web/clipboard";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  buildMerchantInstructions,
  dnsRecordLabel,
  SES_REFUSAL_LOG_MESSAGES,
} from "./ses-setup-instructions";

const STEP_TITLES = [
  "Confirm the domain",
  "Make sure an SES domain exists for it",
  "Have the merchant add the DNS records",
  "Wait for DNS, then re-check",
  "Switch the sender to SES",
  "Confirm it works",
] as const;

const DOMAIN_STEP = 1;
const DNS_STEP = 2;
const WAIT_STEP = 3;
const SWITCH_STEP = 4;

function dkimRecordFound(readiness: SesReadiness): boolean {
  return readiness.dnsRecords.some(
    (record) => record.required && record.status === DnsRecordCheckStatus.OK,
  );
}

function currentStepIndex(readiness: SesReadiness): number {
  switch (readiness.step) {
    case "no_domain":
    case "domain_not_linked":
      return DOMAIN_STEP;
    case "dkim_failed":
      return DNS_STEP;
    case "dns_pending":
      return dkimRecordFound(readiness) ? WAIT_STEP : DNS_STEP;
    case "ready":
      return SWITCH_STEP;
  }
}

function stepState(
  index: number,
  current: number,
  readiness: SesReadiness,
): RedoStepState {
  if (index < current) {
    return "complete";
  }
  if (index > current) {
    return "upcoming";
  }
  return readiness.step === "dkim_failed" ? "partial" : "current";
}

const STEP_ICON: Record<RedoStepState, RedoStep["Icon"]> = {
  complete: CheckCircleIcon,
  current: ArrowCircleRightIcon,
  partial: AlertCircleIcon,
  upcoming: CircleIcon,
};

function stepBadge(state: RedoStepState): RedoBadgeProps {
  switch (state) {
    case "complete":
      return { color: "success", text: "Done", size: "xs" };
    case "current":
      return { color: "brand", text: "Current step", size: "xs" };
    case "partial":
      return { color: "error", text: "Needs fixing", size: "xs" };
    case "upcoming":
      return { color: "gray", text: "Not yet", size: "xs" };
  }
}

const NOT_CHECKED_BADGE: RedoBadgeProps = {
  color: "gray",
  text: "Not checked yet",
  size: "xs",
};

function dnsStatusBadge(status: string | null): RedoBadgeProps {
  if (status === null) {
    return NOT_CHECKED_BADGE;
  }
  switch (status) {
    case DnsRecordCheckStatus.OK:
      return { color: "success", text: "Found", size: "xs" };
    case DnsRecordCheckStatus.MISSING:
      return { color: "warning", text: "Not found", size: "xs" };
    case DnsRecordCheckStatus.MISCONFIGURED:
      return { color: "error", text: "Wrong value", size: "xs" };
    case DnsRecordCheckStatus.ERROR:
      return { color: "gray", text: "Lookup failed", size: "xs" };
    case DnsRecordCheckStatus.NOT_APPLICABLE:
      return { color: "gray", text: "Not applicable", size: "xs" };
    default:
      return NOT_CHECKED_BADGE;
  }
}

async function copyWithToast(text: string, what: string) {
  await copyToClipboard(text);
  toast(`${what} copied`, { variant: "success" });
}

const Code = memo(function Code({ children }: { children: string }) {
  return (
    <Flex basis={0} grow={1} overflowX="auto">
      <Text
        bgColor="tertiary"
        borderRadius="sm"
        fontFamily="monospace"
        fontSize="xs"
        px="sm"
        py="xs"
        textColor="primary"
        whiteSpace="nowrap"
      >
        {children}
      </Text>
    </Flex>
  );
});

const Paragraph = memo(function Paragraph({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Text fontSize="sm" textColor="secondary">
      {children}
    </Text>
  );
});

const FieldLabel = memo(function FieldLabel({ label }: { label: string }) {
  return (
    <Flex shrink={0} w="4xl">
      <Text fontSize="xs" fontWeight="medium" textColor="secondary">
        {label}
      </Text>
    </Flex>
  );
});

const CopyField = memo(function CopyField({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <Flex align="center" gap="sm" w="full">
      <FieldLabel label={label} />
      <Code>{value}</Code>
      <RedoButton
        aria-label={`Copy ${label.toLowerCase()}`}
        hierarchy="tertiary"
        IconLeading={CopyIcon}
        onClick={() => void copyWithToast(value, label)}
        size="sm"
      />
    </Flex>
  );
});

const DnsRecordBlock = memo(function DnsRecordBlock({
  record,
  domain,
}: {
  record: SesDnsRecord;
  domain: string;
}) {
  return (
    <Flex
      borderColor="primary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="sm"
      p="md"
      w="full"
    >
      <Flex align="center" gap="sm" wrap="wrap">
        <Text fontSize="sm" fontWeight="semibold">
          {dnsRecordLabel(record, domain)}
        </Text>
        <RedoBadge
          color={record.required ? "brand" : "gray"}
          size="xs"
          text={record.required ? "Required for SES" : "Recommended"}
        />
        <RedoBadge {...dnsStatusBadge(record.status)} />
      </Flex>
      <Text fontSize="xs" textColor="secondary">
        {record.purpose}
      </Text>
      <CopyField label="Type" value={record.type} />
      <CopyField label="Full host" value={record.host} />
      <CopyField label="Host prefix" value={record.hostPrefix} />
      {record.priority !== null && (
        <CopyField label="Priority" value={record.priority.toString()} />
      )}
      <CopyField label="Value" value={record.value} />
      <Flex align="center" gap="sm">
        <FieldLabel label="TTL" />
        <Text fontSize="xs">Auto (the provider's default)</Text>
      </Flex>
    </Flex>
  );
});

const ConfirmDomainStep = memo(function ConfirmDomainStep({
  sender,
}: {
  sender: CustomEmailSender;
}) {
  const { domain } = sender.sesReadiness;
  return (
    <Flex dir="column" gap="sm">
      <Paragraph>
        The domain is everything after the @ in the sender address. For{" "}
        {sender.email} that is:
      </Paragraph>
      <Flex>
        <Code>{domain || "(the address has no domain)"}</Code>
      </Flex>
      <Paragraph>
        SES needs an email domain that matches this exactly. A sender on a
        subdomain, like support@help.example.com, needs its own domain for
        help.example.com. A verified example.com doesn't cover it.
      </Paragraph>
    </Flex>
  );
});

const DomainExistsStep = memo(function DomainExistsStep({
  teamId,
  readiness,
}: {
  teamId: string;
  readiness: SesReadiness;
}) {
  const marketingPage = `/teams/${teamId}/marketing#email`;
  const merchantEmailDomainPath = `/stores/${teamId}/settings/marketing/email-domain`;
  const openAsMerchant = `/teams/${teamId}/open?next=${encodeURIComponent(merchantEmailDomainPath)}`;
  const merchantSupportEmailPath = `/stores/${teamId}/settings/channels/supportEmail`;
  const openSupportEmailAsMerchant = `/teams/${teamId}/open?next=${encodeURIComponent(merchantSupportEmailPath)}`;
  switch (readiness.step) {
    case "no_domain":
      return (
        <Flex dir="column" gap="sm">
          <Paragraph>
            No SES email domain exists for {readiness.domain}. The admin app
            can't create one; it's created in the merchant app.
          </Paragraph>
          <Paragraph>
            The usual way: the merchant removes this sender and adds it again in
            Settings, then Channels, then Support email. Adding a sender creates
            the SES domain, the DKIM key and the DNS records in step 3
            automatically, and shows the merchant a step-by-step setup guide.
            Staff can do the same as the merchant:{" "}
            <Link target="_blank" to={openSupportEmailAsMerchant}>
              open the merchant's Support email settings
            </Link>
            .
          </Paragraph>
          <Paragraph>
            Alternatively, add {readiness.domain} in Settings, then Marketing,
            then Email domain (
            <Link target="_blank" to={openAsMerchant}>
              open the merchant's Email domain settings
            </Link>
            ).
          </Paragraph>
          <Paragraph>
            If another merchant already set up {readiness.domain}, link it
            instead from{" "}
            <Link to={marketingPage}>this team's Marketing page</Link> (Email &
            deliverability, then Shared email domains).
          </Paragraph>
        </Flex>
      );
    case "domain_not_linked":
      return (
        <Flex dir="column" gap="sm">
          <Paragraph>
            An SES email domain for {readiness.domain} exists (id{" "}
            {readiness.emailDomainId}), but it isn't linked to this team's
            account, so this team can't send with it.
          </Paragraph>
          <Paragraph>
            Linking lets this team's account use the domain and its DKIM key. It
            doesn't create a domain or change DNS. Link it only after you've
            confirmed this merchant owns the domain.
          </Paragraph>
          <Paragraph>
            <Link to={marketingPage}>Open this team's Marketing page</Link>, go
            to Email & deliverability, then Shared email domains, and click Link
            email domain. Pick the team that already has {readiness.domain},
            then choose the domain.
          </Paragraph>
        </Flex>
      );
    case "dns_pending":
    case "dkim_failed":
    case "ready":
      return (
        <Paragraph>
          SES email domain {readiness.domain} (id {readiness.emailDomainId}) is
          linked to this team.
        </Paragraph>
      );
  }
});

const DnsRecordsStep = memo(function DnsRecordsStep({
  sender,
}: {
  sender: CustomEmailSender;
}) {
  const readiness = sender.sesReadiness;
  if (!readiness.emailDomainId) {
    return (
      <Paragraph>
        The records appear here once an SES email domain exists for{" "}
        {readiness.domain}.
      </Paragraph>
    );
  }
  const hasDkim = readiness.dnsRecords.some((record) => record.required);
  return (
    <Flex dir="column" gap="md">
      <Paragraph>
        The merchant adds every record below at their DNS provider. Values come
        from this domain's stored settings, and status is Redo's last DNS check.
      </Paragraph>
      {!hasDkim && (
        <RedoAlert
          color="warning"
          description="Without a DKIM key there is no DKIM record to add. Ask engineering before going further."
          fullWidth
          size="sm"
          title="This domain has no stored DKIM key"
        />
      )}
      <Flex dir="column" gap="xs">
        <Text fontSize="sm" fontWeight="medium">
          Gotchas to tell the merchant
        </Text>
        <Paragraph>
          • Host: many providers add the domain to the name automatically. If
          theirs does, paste only the host prefix. Pasting the full host creates
          a doubled name like redo._domainkey.example.com.example.com.
        </Paragraph>
        <Paragraph>
          • Cloudflare: set every record to DNS only (grey cloud), never
          Proxied.
        </Paragraph>
        <Paragraph>
          • MX: the priority is 10 and goes in its own priority field, not in
          the value.
        </Paragraph>
        <Paragraph>
          • SPF: don't add a second SPF record at the root domain, and don't
          replace the existing one. If one exists, add include:amazonses.com to
          it. The MAIL FROM subdomain's SPF is a separate record on a different
          host.
        </Paragraph>
        <Paragraph>
          • DMARC is optional but recommended: _dmarc TXT v=DMARC1; p=none;.
          Keep an existing DMARC record instead of adding another.
        </Paragraph>
      </Flex>
      {readiness.dnsRecords.map((record) => (
        <DnsRecordBlock
          domain={readiness.domain}
          key={`${record.type}:${record.host}:${record.value}`}
          record={record}
        />
      ))}
      <Flex>
        <RedoButton
          hierarchy="secondary"
          IconLeading={CopyIcon}
          onClick={() =>
            void copyWithToast(
              buildMerchantInstructions(sender.email, readiness),
              "Instructions",
            )
          }
          size="sm"
          text="Copy instructions for merchant"
        />
      </Flex>
    </Flex>
  );
});

function formatStatus(status: string | null): string {
  return status ? status.replace(/_/g, " ") : "not set up";
}

const WaitStep = memo(function WaitStep({
  readiness,
  onRecheck,
  recheckPending,
}: {
  readiness: SesReadiness;
  onRecheck: () => void;
  recheckPending: boolean;
}) {
  const mailFrom = readiness.mailFromDomain
    ? ` MAIL FROM ${readiness.mailFromDomain} is ${formatStatus(readiness.mailFromDomainStatus)}.`
    : "";
  const lastChecked = readiness.dnsLastCheckedAt
    ? ` Last checked ${readiness.dnsLastCheckedAt.toLocaleString()}.`
    : " Not checked yet.";
  return (
    <Flex dir="column" gap="sm">
      <Paragraph>
        DNS changes take anywhere from a few minutes to 48 hours to show up.
        Redo re-checks pending domains linked to a team every 20 minutes (at
        :18, :38, and :58 past the hour). Re-check DNS checks now.
      </Paragraph>
      <Paragraph>
        {`DKIM is ${formatStatus(readiness.dkimStatus)}.${mailFrom}${lastChecked}`}
      </Paragraph>
      <Paragraph>
        Pending means SES hasn't seen the DKIM record yet: wait, then re-check.
        Failed means SES stopped looking: fix the record, then re-check. When a
        record shows Not found or Wrong value, look for a typo in the host, a
        doubled domain (example.com.example.com), a Cloudflare record left on
        Proxied, or a value that was cut off or changed when pasted.
      </Paragraph>
      <Flex>
        <RedoButton
          disabled={!readiness.linkedToTeam || recheckPending}
          hierarchy="secondary"
          IconLeading={RefreshIcon}
          onClick={onRecheck}
          pending={recheckPending}
          size="sm"
          text="Re-check DNS"
        />
      </Flex>
    </Flex>
  );
});

const SwitchStep = memo(function SwitchStep() {
  return (
    <Paragraph>
      Once DKIM is verified, Use SES in the table above turns on. Switching
      changes outbound only: replies from this address send through SES. Inbound
      forwarding doesn't change, and the existing Postmark inbound address keeps
      receiving mail.
    </Paragraph>
  );
});

const ConfirmStep = memo(function ConfirmStep({
  sender,
}: {
  sender: CustomEmailSender;
}) {
  return (
    <Flex dir="column" gap="sm">
      <Paragraph>
        Send a test reply from this team's helpdesk using {sender.email}. Check
        that the draft is sent and that the email arrives in the recipient's
        inbox.
      </Paragraph>
      <Paragraph>
        If it doesn't send, search Datadog logs for these messages with the team
        id:
      </Paragraph>
      {SES_REFUSAL_LOG_MESSAGES.map((message) => (
        <Flex key={message}>
          <Code>{message}</Code>
        </Flex>
      ))}
    </Flex>
  );
});

/** Numbered guide to getting a sender's domain ready for SES, driven by `sesReadiness.step`. */
export const SesSetupGuide = memo(function SesSetupGuide({
  teamId,
  sender,
  onRecheck,
  recheckPending,
}: {
  teamId: string;
  sender: CustomEmailSender;
  onRecheck: () => void;
  recheckPending: boolean;
}) {
  const readiness = sender.sesReadiness;
  const current = currentStepIndex(readiness);
  const bodies: ReactNode[] = [
    <ConfirmDomainStep key="confirm-domain" sender={sender} />,
    <DomainExistsStep
      key="domain-exists"
      readiness={readiness}
      teamId={teamId}
    />,
    <DnsRecordsStep key="dns" sender={sender} />,
    <WaitStep
      key="wait"
      onRecheck={onRecheck}
      readiness={readiness}
      recheckPending={recheckPending}
    />,
    <SwitchStep key="switch" />,
    <ConfirmStep key="confirm" sender={sender} />,
  ];
  const steps = STEP_TITLES.map((title, index) => {
    const state = stepState(index, current, readiness);
    return { title, state, body: bodies[index] };
  });

  return (
    <RedoCollapseSubsection title={`How to set up SES for ${sender.email}`}>
      <Flex dir="column" gap="lg" w="full">
        <RedoStepIndicator
          activeIndex={current}
          direction="vertical"
          lead="icon"
          steps={steps.map(({ title, state }, index) => ({
            id: title,
            label: `${index + 1}. ${title}`,
            state,
            Icon: STEP_ICON[state],
          }))}
        />
        {steps.map(({ title, state, body }, index) => (
          <RedoCollapseSubsection
            defaultCollapsed={state === "complete" || index > current + 1}
            key={title}
            title={`${index + 1}. ${title}`}
            tooltip={<RedoBadge {...stepBadge(state)} />}
          >
            {body}
          </RedoCollapseSubsection>
        ))}
      </Flex>
    </RedoCollapseSubsection>
  );
});
